import { Renderer2 } from '@angular/core';

import { isNil } from '@gfe/core/utils/shared-utils';

import { PlatformMetaKeyUtils } from './platform-meta-key.utils';

/**
 * This class is used to handle shortcuts IMPORTANT: Remember to call the dispose method
 * when the component is destroyed.
 */
export class Shortcut {
  readonly shortcut: string;
  private isDisabled: boolean = false;

  disposeListener: (() => void) | undefined;
  action: (event: Event) => void;

  /**
   * @param shortcut Refer to
   *   https://www.w3.org/TR/DOM-Level-3-Events-key/#named-key-attribute-values to see all
   *   possible values for shortcut
   * @param action Function that should be called when the shortcut is triggered
   */
  constructor(shortcut: string, action: (event: Event) => void) {
    this.shortcut = PlatformMetaKeyUtils.parseMetaKey(shortcut);
    this.action = action;
  }

  init(renderer: Renderer2): void {
    if (isNil(this.shortcut)) {
      return;
    }
    if (isNil(this.disposeListener)) {
      this.disposeListener = renderer.listen(
        'window',
        `keydown.${this.shortcut!}`,
        (event: Event) => {
          if (!(event instanceof KeyboardEvent)) {
            return;
          }
          if (this.isDisabled) {
            return;
          }

          // we ignore the shortcut if the user is typing something in a dialog box
          if (
            event.target instanceof HTMLInputElement ||
            event.target instanceof HTMLTextAreaElement
          ) {
            return;
          }

          event.preventDefault();
          this.action(event);
        }
      );
    }
  }

  disable(): void {
    this.isDisabled = true;
  }

  enable(): void {
    this.isDisabled = false;
  }

  dispose(): void {
    if (!isNil(this.disposeListener)) {
      this.disposeListener();
      this.disposeListener = undefined;
    }
  }

  toString(): string {
    return this.shortcut
      .split('.')
      .map((key) => this.formatKey(key))
      .join('+');
  }

  /** Refer to the doc at the top of this file if you need to add more keys */
  private formatKey(keyName: string): string {
    switch (keyName) {
      case 'Meta':
        return PlatformMetaKeyUtils.getMetaKey();
      case 'Control':
        return this.displayForMacOrPc('⌃', 'Ctrl');
      case 'Enter':
        return '↵';
      case 'Alt':
        return this.displayForMacOrPc('⌥', 'Alt');
      case 'Shift':
        return '⇧';
      default:
        return keyName.length === 1 ? keyName.toUpperCase() : keyName;
    }
  }

  private displayForMacOrPc(macKey: string, pcKey: string): string {
    return PlatformMetaKeyUtils.isMac() ? macKey : pcKey;
  }
}
